import React, { useState } from "react";
import axios from "axios";
import { Button } from "../../../../components/ui/button";
import { ClipboardList, Loader2, X, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fetches eBay required/recommended Item Specifics for the currently-selected
 * category (#ebay_category_id) and shows them in a modal. Each aspect has a
 * "+ Aggiungi" button that appends a "Name: " line to the #ebay_aspects
 * textarea so the admin can fill values quickly without typing the key.
 */
export const EbayAspectsHelperButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const openModal = async () => {
    const catId = (document.getElementById("ebay_category_id")?.value || "").trim();
    if (!catId) {
      setError("Imposta prima la Categoria eBay");
      setOpen(true);
      return;
    }
    setOpen(true);
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const token = sessionStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/ebay/category-aspects?category_id=${encodeURIComponent(catId)}`, { headers });
      if (!res.data?.ok) {
        setError(res.data?.error || "Errore eBay");
      } else {
        setData(res.data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const insertAspect = (name) => {
    const ta = document.getElementById("ebay_aspects");
    if (!ta) return;
    const current = ta.value || "";
    // Skip if already present (case-insensitive key check)
    const existing = current.split("\n").map((l) => l.split(":")[0].trim().toLowerCase());
    if (existing.includes(name.toLowerCase())) return;
    const sep = current && !current.endsWith("\n") ? "\n" : "";
    ta.value = `${current}${sep}${name}: `;
    ta.focus();
    // Move cursor to end
    ta.selectionStart = ta.selectionEnd = ta.value.length;
  };

  const usageLabel = (u) =>
    u === "REQUIRED"
      ? { text: "Obbligatorio", className: "bg-red-100 text-red-700 border-red-300" }
      : u === "RECOMMENDED"
      ? { text: "Raccomandato", className: "bg-amber-100 text-amber-700 border-amber-300" }
      : { text: "Opzionale", className: "bg-gray-100 text-gray-600 border-gray-300" };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={openModal}
        className="h-8 px-3 text-xs bg-purple-50 hover:bg-purple-100 border-purple-300 text-purple-700 whitespace-nowrap"
        data-testid="ebay-aspects-helper-btn"
      >
        <ClipboardList className="w-3 h-3 mr-1" />
        Aspects categoria
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-900">
                Item Specifics richiesti{data ? ` — cat ${data.category_id}` : ""}
              </h3>
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-900">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-4 flex-1">
              {loading && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Caricamento da eBay…
                </div>
              )}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  ⚠️ {error}
                </div>
              )}
              {data && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600 mb-2">
                    Clicca su <b>Aggiungi</b> per inserire l'aspect nel campo "Specifiche eBay" del form.
                    Compila il valore manualmente seguendo gli esempi suggeriti.
                  </p>
                  {data.aspects.map((asp) => {
                    const lbl = usageLabel(asp.usage);
                    return (
                      <div
                        key={asp.name}
                        className="flex items-start gap-3 p-2 border border-gray-200 rounded hover:bg-gray-50"
                      >
                        <span className={`px-1.5 py-0.5 text-[10px] font-bold border rounded ${lbl.className} mt-1 whitespace-nowrap`}>
                          {lbl.text}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">{asp.name}</div>
                          {asp.values && asp.values.length > 0 && (
                            <div className="text-[10px] text-gray-500 mt-0.5 truncate">
                              <b>Valori suggeriti:</b> {asp.values.slice(0, 5).join(", ")}
                              {asp.values.length > 5 && ` … (+${asp.values.length - 5})`}
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => insertAspect(asp.name)}
                          className="h-7 px-2 text-xs whitespace-nowrap"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Aggiungi
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t flex justify-end">
              <Button type="button" size="sm" onClick={() => setOpen(false)}>
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
